import React, { useMemo, useState } from 'react';

import { DefaultTable, LinkCell } from '@hud/web-components/Table/Table';
import {
  createColumnHelper,
  getCoreRowModel,
  getSortedRowModel,
  SortingState,
  Updater,
  useReactTable,
} from '@tanstack/react-table';
import { Job } from '@hud/portal-dtos/pr-comparisons.dto';
import { useGlobalQueryString } from '../../../hooks/useGlobalFilter';
import TableContainer from '@hud/web-components/Container/TableContainer';
import SearchRow from '@hud/web-components/SearchRow/SearchRow';
import { Search } from '@hud/web-components/Search/Search';
import Skeleton from '@hud/web-components/Skeleton/Skeleton';
import { ItemCounter } from '@hud/web-components/ItemCounter/ItemCounter';
import { HorizontalContainer } from '@hud/web-components/Container/HorizontalContainer';
import { DEFAULT_PAGE_SIZE } from '../../../consts';
import Pagination from '@hud/web-components/Pagination/Pagination';
import {
  formatDatetime,
  renderDatetime,
} from '@hud/web-components/Table/CellRenderers';
import LongContentPopover from '@hud/web-components/LongContentPopover/LongContentPopover';
import { formatMetricValue } from '../../../utils';
import { FlagFilter } from '@hud/web-components/Filters/Filters';
import Menu from '@hud/web-components/Menu/Menu';
import {
  FiltersIcon,
  FiltersMenuButton,
} from '../../../components/FunctionsTable/FunctionsTable.styles';
import { renderEndpointByPathAndJob } from '../../../components/endpointsCellRenderer/EndpointByPathCellRenderer';

const columnHelper = createColumnHelper<Job>();

interface Filters {
  onlyShowDegraded: boolean;
}

type FilterKeys = keyof Filters;

interface Filter<T extends FilterKeys> {
  type: T;
  value: Filters[T];
}

export interface JobsTableProps {
  jobs: Job[];
  total?: number;
  countFiltered?: number;
  loading: boolean;

  searchValue: string;
  onSearchChange: (searchValue: string) => void;

  sortingState: SortingState;
  onSortingChange: (updater: Updater<SortingState>) => void;

  onlyShowDegraded: boolean;
  onOnlyShowDegradedChange: (value: boolean) => void;

  pages: number;
  currentPage: number;
  onPageChange: (page: number) => void;
}

export default function JobsTable(props: JobsTableProps) {
  const globalFilters = useGlobalQueryString();

  const initializeFilters = (): Filter<FilterKeys>[] => {
    const filters: Filter<FilterKeys>[] = [];
    if (props.onlyShowDegraded) {
      filters.push({
        type: 'onlyShowDegraded',
        value: true,
      });
    }
    return filters;
  };

  const [activeFilters, setActiveFilters] =
    useState<Filter<FilterKeys>[]>(initializeFilters);

  const handleFilterChange = (filterType: FilterKeys, isActive: boolean) => {
    const newFilters = activeFilters.filter(
      (filter) => filter.type !== filterType,
    );
    if (isActive) {
      newFilters.push({
        type: filterType,
        value: isActive,
      });
    }
    setActiveFilters(newFilters);
  };

  const toggleOnlyShowDegraded = (isActive: boolean) => {
    handleFilterChange('onlyShowDegraded', isActive);

    props.onOnlyShowDegradedChange(isActive);
  };

  const columns = useMemo(() => {
    return [
      columnHelper.accessor('id', {
        header: 'Job ID',
        minSize: 80,
        maxSize: 300,
        cell: (cell) => {
          return (
            <LinkCell
              to={`/explore/prs/${cell.row.original.id}?${globalFilters}`}
            >
              <LongContentPopover>{cell.getValue()}</LongContentPopover>
            </LinkCell>
          );
        },
        meta: {
          copyable: true,
          fitFormatter: (cell) => cell.getValue(),
        },
      }),
      columnHelper.accessor('prId', {
        header: 'PR ID',
        minSize: 80,
        maxSize: 200,
        meta: {
          copyable: true,
          fitFormatter: (cell) => cell.getValue(),
        },
      }),
      columnHelper.accessor('endedAt', {
        header: 'Ended At',
        minSize: 80,
        maxSize: 120,
        cell: renderDatetime(true),
        meta: {
          fitFormatter: formatDatetime(true),
          rightAlign: true,
        },
      }),
      columnHelper.accessor('degradedEndpoints', {
        header: 'Deg EPs',
        minSize: 80,
        maxSize: 120,
        meta: {
          copyable: false,
          rightAlign: true,
        },
      }),
      columnHelper.accessor('topDegradedEndpointService', {
        header: 'Top Deg Service',
        minSize: 50,
        maxSize: 150,
        cell: (cell) => {
          return (
            <LongContentPopover>{cell.getValue()?.name}</LongContentPopover>
          );
        },
      }),
      columnHelper.accessor('topDegradedEndpoint', {
        header: 'Top Deg',
        minSize: 200,
        maxSize: 500,
        cell: renderEndpointByPathAndJob(),
      }),
      columnHelper.accessor('p90Degradation.value', {
        header: 'Top Deg P90',
        minSize: 150,
        maxSize: 300,
        cell: (cell) => {
          const baseP90 = cell.row.original.baseP90Duration ?? 0;
          if (cell.getValue() === undefined || cell.getValue() === null) {
            return '';
          }
          const baseP90normalized = formatMetricValue({
            value: baseP90,
            format: 'duration',
          });

          const topP90DegNormalized = formatMetricValue({
            value: cell.getValue(),
            format: 'duration',
          });
          return `${baseP90normalized} -> ${topP90DegNormalized}`;
        },
        meta: {
          copyable: false,
          rightAlign: true,
        },
      }),
      columnHelper.accessor('p90Degradation.rate', {
        header: 'Top Deg P90 %',
        minSize: 60,
        maxSize: 200,
        cell: (cell) => {
          if (cell.getValue() === undefined || cell.getValue() === null) {
            return '';
          }
          const normalized = formatMetricValue({
            value: cell.getValue(),
            format: 'percent',
          });
          return normalized;
        },
        meta: {
          rightAlign: true,
        },
      }),
    ];
  }, [globalFilters]);

  const table = useReactTable({
    data: props.jobs,
    columns,
    state: {
      sorting: props.sortingState,
    },

    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),

    onSortingChange: props.onSortingChange,

    columnResizeMode: 'onChange',
    manualSorting: true,
    manualPagination: true,
    manualFiltering: true,
    enableSortingRemoval: false,

    defaultColumn: {},
  });

  const hasFilters = activeFilters.length > 0;

  return (
    <TableContainer.Container>
      <TableContainer.Header>
        <SearchRow>
          <Search
            inline
            value={props.searchValue}
            onSearchChange={props.onSearchChange}
          />
          {activeFilters.map((filter) => {
            switch (filter.type) {
              case 'onlyShowDegraded':
                return (
                  <FlagFilter
                    key={filter.type}
                    label="Show Degraded Only"
                    onClear={() => toggleOnlyShowDegraded(false)}
                  />
                );
              default:
                return null;
            }
          })}
          <Menu.Menu>
            <Menu.Target>
              <div>
                <FiltersMenuButton>
                  <FiltersIcon
                    icon={hasFilters ? 'menu.plus' : 'other.filters'}
                    size={hasFilters ? '14px' : '20px'}
                  />
                  {!hasFilters ? 'Filters' : ''}
                </FiltersMenuButton>
              </div>
            </Menu.Target>
            <Menu.Dropdown>
              <Menu.Item onClick={() => toggleOnlyShowDegraded(true)}>
                Show Degraded Only
              </Menu.Item>
            </Menu.Dropdown>
          </Menu.Menu>
        </SearchRow>
        {props.loading ? (
          <Skeleton width={100} />
        ) : (
          <ItemCounter
            total={props.total ?? 0}
            filtered={props.countFiltered ?? 0}
            activeCounter={true}
          />
        )}
      </TableContainer.Header>
      <HorizontalContainer>
        <DefaultTable
          table={table}
          label="Jobs Table"
          loading={props.loading}
          loadingRows={DEFAULT_PAGE_SIZE}
        />
      </HorizontalContainer>
      <TableContainer.Footer>
        <Pagination
          pages={props.pages}
          currentPage={props.currentPage}
          onPageChange={props.onPageChange}
          loading={props.loading}
        />
      </TableContainer.Footer>
    </TableContainer.Container>
  );
}
